import { apiClient } from './client';

export type FleetType = 'exploration' | 'trade' | 'war' | 'defence';
export type FleetOrderType = 'move' | 'patrol' | 'trade' | 'attack' | 'intercept';

export interface SpaceMapStar {
  space_map_star_id: string;
  star_index: number;
  grid_x: number;
  grid_y: number;
  star_type: string;
  energy_output: number;
  is_major: boolean;
  is_home_slot: boolean;
  visible?: boolean;
  properties?: Record<string, unknown>;
}

export type SpaceStructureType = 'port' | 'harvester';

export interface SpacePort {
  space_port_id: string;
  planet_id: string;
  name: string;
  grid_x: number;
  grid_y: number;
  level: number;
  status: string;
  port_type: SpaceStructureType;
  is_home: boolean;
  defence_bonus: number;
}

export interface Fleet {
  fleet_id: string;
  planet_id: string;
  name: string;
  fleet_type: FleetType;
  status: string;
  grid_x: number;
  grid_y: number;
  destination_x?: number | null;
  destination_y?: number | null;
  arrival_day?: number | null;
  attack: number;
  defence: number;
  shields: number;
  speed: number;
  cargo_capacity: number;
  cargo_credits: number;
  cargo_energy: number;
  scan_range: number;
}

export interface SpaceTechnology {
  space_technology_definition_id: string;
  tech_group: string;
  tier: number;
  name: string;
  description?: string | null;
  credit_cost: number;
  energy_cost: number;
  research_days: number;
  effects: Record<string, number>;
  status: 'available' | 'researching' | 'completed';
  locked: boolean;
}

export interface SpaceEventItem {
  space_event_id: string;
  event_type: string;
  severity: string;
  title: string;
  message: string;
  created_day: number;
  read_at?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface SpaceTradeOffer {
  space_trade_offer_id: string;
  seller_planet_id: string;
  buyer_planet_id?: string | null;
  offered_resource: 'credits' | 'energy';
  offered_amount: number;
  requested_resource: 'credits' | 'energy';
  requested_amount: number;
  status: string;
  created_day: number;
  expires_day?: number | null;
}

export interface SpaceMapResponse {
  planetId: string;
  currentDay: number;
  activePlayDay: number;
  spaceUnlocked: boolean;
  grid: { size: number };
  settings: {
    spaceEnabled: boolean;
    tradeEnabled: boolean;
    conflictEnabled: boolean;
    communicationsEnabled: boolean;
    seasonMode: string;
    seasonEndDay?: number | null;
    homeSafeZoneRadius: number;
  };
  resources: {
    credits: number;
    energy: number;
    lifetimeEnergyHarvested: number;
  };
  stars: SpaceMapStar[];
  ports: SpacePort[];
  fleets: Fleet[];
  technologies: SpaceTechnology[];
  tradeOffers: SpaceTradeOffer[];
  events: SpaceEventItem[];
  fog: { visibleCells: string[] };
  energyInfluence: Array<{ starId: string; planetId: string; portId: string; energyPerDay: number; influence: number }>;
}

export const spaceConquestApi = {
  getMap: () => apiClient.get<{ success: boolean; data: SpaceMapResponse }>('/space/map'),
  getEvents: () => apiClient.get<{ success: boolean; data: SpaceEventItem[] }>('/space/events'),
  createPort: (input: { gridX: number; gridY: number; name?: string; structureType?: SpaceStructureType }) =>
    apiClient.post<{ success: boolean; data: unknown }>('/space/ports', input),
  upgradePort: (portId: string) =>
    apiClient.post<{ success: boolean; data: unknown }>(`/space/ports/${portId}/upgrade`),
  createFleet: (input: { fleetType: FleetType; originPortId?: string; name?: string }) =>
    apiClient.post<{ success: boolean; data: unknown }>('/space/fleets', input),
  orderFleet: (fleetId: string, input: { orderType?: FleetOrderType; gridX?: number; gridY?: number; targetPortId?: string; cargoCredits?: number; cargoEnergy?: number }) =>
    apiClient.post<{ success: boolean; data: unknown }>(`/space/fleets/${fleetId}/orders`, input),
  interceptFleet: (fleetId: string, targetFleetId: string) =>
    apiClient.post<{ success: boolean; data: unknown }>(`/space/fleets/${fleetId}/intercept`, { targetFleetId }),
  researchTechnology: (definitionId: string) =>
    apiClient.post<{ success: boolean; data: unknown }>(`/space/technologies/${definitionId}/research`),
  getTradeOffers: () => apiClient.get<{ success: boolean; data: SpaceTradeOffer[] }>('/space/trade-offers'),
  postTradeOffer: (input: { offeredResource?: 'credits' | 'energy'; offeredAmount?: number; requestedResource?: 'credits' | 'energy'; requestedAmount?: number; offerId?: string }) =>
    apiClient.post<{ success: boolean; data: unknown }>('/space/trade-offers', input),
};
